import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, getMangaByCategory } from '@/mocks/manga';
import { siteInfo } from '@/mocks/manga';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const mangaList = slug ? getMangaByCategory(slug) : [];
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
          <Link to="/" className="text-accent-600 hover:text-accent-700 mt-4 inline-block text-sm">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const sortedList = [...mangaList].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    if (sortBy === 'new') return new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime();
    return b.rating - a.rating;
  });

  const categoryLabel = currentCategory.label;
  const pageTitle = `${categoryLabel} | ${siteInfo.name} | 免费全集下拉式随心看`;
  const pageDesc = `${siteInfo.name}${categoryLabel}专区，精选${categoryLabel}腐漫推荐，${currentCategory.description}。免费全集下拉式阅读，每日更新热门${categoryLabel}资源。`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageTitle,
      "description": pageDesc,
      "url": `https://fumanhua.com/category/${slug}`,
      "isPartOf": {
        "@type": "WebSite",
        "name": siteInfo.name,
        "url": "https://fumanhua.com"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://fumanhua.com" },
        { "@type": "ListItem", "position": 2, "name": categoryLabel }
      ]
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${categoryLabel},腐漫画网,好看腐漫,热门腐漫推荐,免费全集`}
        canonical={`https://fumanhua.com/category/${slug}`}
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: categoryLabel }]} />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {categoryLabel}
                </h1>
                <p className="text-foreground-500 text-sm mt-1">{currentCategory.description}</p>
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                {[
                  { key: 'hot' as const, label: '最热' },
                  { key: 'new' as const, label: '最新' },
                  { key: 'rating' as const, label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-accent-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-accent-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>

            {sortedList.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 pb-14">
                {sortedList.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}